import React from 'react';
import { twMerge } from 'tailwind-merge';

import Button from 'components/partials/button/button';
import Checkbox from 'components/partials/checkbox/checkbox';
import ReportAssignmentUsers from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment-users';
import Icons from 'assets/icons';

import { getBorderClass } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.utils';
import { USER_ASSIGNMENT_STATUS } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment.constants';
import { useExpandable } from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-exandable';

import type {
  ReportData,
  ReportUser,
} from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/hooks/use-report-data';

interface UserAccessChangePayload {
  userId: string | number;
  hasAccess: boolean;
}

interface ReportAssignmentRowProps {
  report: ReportData;
  fetchReportUsers: (reportId: string | number) => Promise<ReportUser[] | null>;
  onReportAccessChange: (
    reportId: string | number,
    value: boolean
  ) => void | Promise<void>;
  onDataIssueChange: (
    reportId: string | number,
    value: boolean
  ) => void | Promise<void>;
  onUserAccessChange: (
    reportId: string | number,
    userData: UserAccessChangePayload
  ) => void;
  reportUsers: ReportUser[] | undefined;
  isFirst: boolean;
  isLast: boolean;
  isLoading: boolean;
  isInternalAdmin?: boolean;
}

const ReportAssignmentRow = ({
  report,
  fetchReportUsers,
  onReportAccessChange,
  onDataIssueChange,
  onUserAccessChange,
  reportUsers,
  isFirst,
  isLast,
  isLoading,
  isInternalAdmin = false,
}: ReportAssignmentRowProps): React.JSX.Element => {
  const { isExpanded, isPending, isClosing, toggle } = useExpandable(
    (reportId: string | number) => fetchReportUsers(reportId)
  );

  const handleToggle = (): void => {
    if (isLoading) return;
    toggle(report.id, !!reportUsers);
  };

  const borderClass = getBorderClass(isFirst, isLast);
  const isDisabled = isLoading || isInternalAdmin;

  return (
    <>
      <tr className="transition-colors duration-200">
        <td className={`py-3 w-[5%] ${borderClass}`}>
          <div className="flex justify-center">
            <Button
              variant="reserve-text"
              size="xs"
              onClick={handleToggle}
              aria-label={isExpanded ? 'Collapse users' : 'Expand users'}
              iconBefore={
                <div
                  className={`transform transition-transform duration-300 text-navy-500 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                >
                  {isExpanded ? (
                    <Icons.OutlinedMinus className="size-5" />
                  ) : (
                    <Icons.OutlinedPlus
                      className={twMerge(
                        'size-5',
                        isPending ? 'opacity-50' : ''
                      )}
                    />
                  )}
                </div>
              }
              className="p-0"
              disabled={isLoading || isPending}
            />
          </div>
        </td>
        <td className={`py-3 px-4 w-[55%] ${borderClass}`}>
          <span className="font-semibold text-gray-700">{report.title}</span>
        </td>
        <td className={`py-3 px-4 text-center w-[20%] ${borderClass}`}>
          <Checkbox
            indeterminate={
              report.userAssignmentStatus ===
              USER_ASSIGNMENT_STATUS.PARTIALLY_ASSIGNED
            }
            labelFor={`report-access-${report.id}`}
            label=""
            checkboxValue={String(report.id)}
            checked={
              report.userAssignmentStatus ===
              USER_ASSIGNMENT_STATUS.ALL_ASSIGNED
            }
            onChange={() =>
              onReportAccessChange(
                report.id,
                report.userAssignmentStatus !==
                  USER_ASSIGNMENT_STATUS.ALL_ASSIGNED
              )
            }
            disabled={isDisabled}
            customStylesLabel="flex justify-center"
            aria-label={`Report access for ${report.title}`}
          />
        </td>
        <td className={`py-3 px-4 text-center w-[20%] ${borderClass}`}>
          <Checkbox
            labelFor={`data-issue-${report.id}`}
            label=""
            checkboxValue={String(report.id)}
            checked={!!report.hasDataIssue}
            onChange={() => onDataIssueChange(report.id, !report.hasDataIssue)}
            disabled={isDisabled}
            customStylesLabel="flex justify-center"
            aria-label={`Data issue visibility for ${report.title}`}
          />
        </td>
      </tr>

      {(isExpanded || isClosing) && reportUsers && (
        <ReportAssignmentUsers
          reportId={report.id}
          reportUsers={reportUsers}
          onUserAccessChange={onUserAccessChange}
          isDisabled={isDisabled}
          isClosing={isClosing}
        />
      )}
    </>
  );
};

export default React.memo(ReportAssignmentRow);
